<!--
  AGENDA — prenotazioni e spostamenti interni nella stessa pagina.

  Un operatore non pensa «prenotazioni» e «spostamenti» separatamente, pensa
  «cosa succede martedì»: per questo stanno nella stessa pagina.

  Due viste sullo STESSO contenuto, con mestieri diversi:
    - CALENDARIO risponde a «quando»;
    - ELENCO risponde a «trovami questa targa» e «fammi agire».
  Per questo la ricerca e i filtri di stato stanno solo nell'elenco.

  ⚠️ I SERVIZI NON SONO VOCI DI MENU, sono un filtro qui dentro. È ciò che
  rende la navigazione indipendente dalle abilitazioni del partner: un
  servizio in più non aggiunge una voce, aggiunge una pastiglia.

  ⚠️ UNA RIGA È UNA TRATTA, non una prenotazione (vedi `agendaRows.js`).
-->
<template>
  <div
    v-loading="loading"
    class="agenda"
  >
    <!-- ---------- intestazione ---------- -->
    <div class="agenda__head">
      <h1 class="agenda__title">
        Agenda
      </h1>

      <div class="agenda__spacer" />

      <!-- interruttore di vista -->
      <div
        class="agenda__views"
        role="group"
        aria-label="Vista"
      >
        <button
          type="button"
          class="agenda__view"
          :class="{ 'is-active': vista === 'calendario' }"
          @click="mostraVista('calendario')"
        >
          Calendario
        </button>
        <button
          type="button"
          class="agenda__view"
          :class="{ 'is-active': vista === 'elenco' }"
          @click="mostraVista('elenco')"
        >
          Elenco
        </button>
      </div>

      <!-- navigazione del mese: vale per entrambe le viste, la finestra è una -->
      <div class="agenda__month">
        <button
          type="button"
          class="agenda__nav"
          aria-label="Mese precedente"
          @click="spostaMese(-1)"
        >
          ‹
        </button>
        <span class="agenda__monthLbl">{{ etichettaMese }}</span>
        <button
          type="button"
          class="agenda__nav"
          aria-label="Mese successivo"
          @click="spostaMese(1)"
        >
          ›
        </button>
      </div>
    </div>

    <!-- ---------- filtri ---------- -->
    <div class="agenda__filters">
      <button
        type="button"
        class="agenda__pill"
        :class="{ 'is-on': servizio === 'tutti' }"
        @click="servizio = 'tutti'"
      >
        Tutti <span class="agenda__count">{{ righe.length }}</span>
      </button>
      <button
        v-for="s in serviziDisponibili"
        :key="s.value"
        type="button"
        class="agenda__pill"
        :class="{ 'is-on': servizio === s.value }"
        @click="servizio = s.value"
      >
        <span
          class="agenda__dot"
          :style="{ background: s.color }"
        />
        {{ s.label }} <span class="agenda__count">{{ s.n }}</span>
      </button>

      <div class="agenda__spacer" />

      <!-- Ricerca e stato servono a TROVARE: hanno senso solo nell'elenco. -->
      <template v-if="vista === 'elenco'">
        <div class="agenda__search">
          <input
            v-model="cerca"
            type="text"
            placeholder="Targa, cliente o n° booking"
          >
        </div>
        <select
          v-model="stato"
          class="agenda__select"
        >
          <option value="tutti">
            Tutti gli stati
          </option>
          <option value="attive">
            Attive
          </option>
          <option value="concluse">
            Concluse
          </option>
        </select>
      </template>
    </div>

    <!-- Righe troncate: si dice, non si mostra un elenco più corto in silenzio -->
    <div
      v-if="troncato"
      class="agenda__warn"
    >
      <span class="agenda__warnIco">⚠</span>
      <span>
        Il server ha restituito solo una parte delle righe di questo mese.
        Quello che vedi è incompleto: restringi il periodo.
      </span>
    </div>

    <!-- BARRA DELL'ELENCO. A SINISTRA, sempre nello stesso punto, c'è la via del
         ritorno: dal giorno si torna al calendario, da «tutto il mese» si torna
         al giorno.

         Si arriva al giorno cliccando una cella piena del calendario: l'elenco
         è ristretto a quel giorno. Va detto — un elenco filtrato che non
         dichiara il filtro fa sembrare che manchino delle righe. -->
    <div
      v-if="vista === 'elenco' && giornoScelto"
      class="agenda__daybar"
    >
      <button
        type="button"
        class="agenda__back"
        @click="tornaAlCalendario"
      >
        <span aria-hidden="true">‹</span> Calendario
      </button>
      <span class="agenda__daybarTxt">
        Solo <strong>{{ giornoEsteso }}</strong> · {{ righeFiltrate.length }}
        {{ righeFiltrate.length === 1 ? 'riga' : 'righe' }}
      </span>
      <div class="agenda__spacer" />
      <button
        type="button"
        class="agenda__daybarBtn"
        @click="aggiungiInGiornata"
      >
        + Aggiungi in questa giornata
      </button>
      <button
        type="button"
        class="agenda__daybarLink"
        @click="mostraMese"
      >
        Mostra tutto il mese
      </button>
    </div>
    <div
      v-else-if="vista === 'elenco'"
      class="agenda__daybar"
    >
      <!-- In alto a sinistra c'è SEMPRE la via del ritorno al calendario: è la
           vista da cui si parte. Se si veniva da un giorno preciso, quello resta
           raggiungibile con un secondo pulsante. -->
      <button
        type="button"
        class="agenda__back"
        @click="tornaAlCalendario"
      >
        <span aria-hidden="true">‹</span> Calendario
      </button>
      <button
        v-if="giornoRitorno"
        type="button"
        class="agenda__daybarLink"
        @click="tornaAlGiorno"
      >
        Torna a {{ giornoRitornoEsteso }}
      </button>
      <span class="agenda__daybarTxt">
        Tutto <strong>{{ etichettaMese }}</strong> · {{ righeFiltrate.length }}
        {{ righeFiltrate.length === 1 ? 'riga' : 'righe' }}
      </span>
    </div>

    <!-- ================= ELENCO ================= -->
    <AgendaRighe
      v-if="vista === 'elenco'"
      :righe="righeFiltrate"
      :loading="loading"
      :apribile="apribile"
      vuoto="Niente in agenda per questo periodo."
      @apri="apri"
    />

    <!-- ================= CALENDARIO ================= -->
    <div
      v-else
      class="agenda__cal"
    >
      <div
        v-for="d in ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']"
        :key="d"
        class="agenda__dow"
      >
        {{ d }}
      </div>

      <!-- Un giorno si clicca, e cosa succede dipende da cosa contiene:
           vuoto -> si sceglie il servizio da creare; pieno -> si apre
           l'elenco di quel giorno, da cui si puo' comunque aggiungere. -->
      <div
        v-for="c in celle"
        :key="c.key"
        class="agenda__day"
        :class="{ 'is-out': !c.nelMese, 'is-today': c.oggi, 'is-clickable': true }"
        role="button"
        tabindex="0"
        :aria-label="etichettaGiorno(c)"
        @click="apriGiorno(c)"
        @keydown.enter.prevent="apriGiorno(c)"
        @keydown.space.prevent="apriGiorno(c)"
      >
        <span class="agenda__num">{{ c.numero }}</span>
        <span
          v-if="!c.eventi.length"
          class="agenda__add"
          aria-hidden="true"
        >+</span>
        <div
          v-for="e in c.eventi.slice(0, 3)"
          :key="e.key"
          class="agenda__ev"
          :style="{ borderLeftColor: coloreServizio(e.service) }"
        >
          <span class="agenda__evH">{{ ora(e.start) }}</span>
          {{ e.plate || e.customer || '—' }}
        </div>
        <span
          v-if="c.eventi.length > 3"
          class="agenda__more"
        >+{{ c.eventi.length - 3 }} altre</span>
      </div>
    </div>

    <!-- ---------- scelta del servizio per un giorno ----------
         Le stesse opzioni del passo 1 di «Nuova prenotazione», con la stessa
         regola sulle abilitazioni: arrivano entrambe da services/servizi.js,
         cosi' non possono divergere. -->
    <el-dialog
      v-model="sceltaAperta"
      :title="`Cosa vuoi aggiungere${giornoCreazioneEsteso ? ' — ' + giornoCreazioneEsteso : ''}?`"
      width="min(560px, 92vw)"
    >
      <div
        v-if="!serviziAttiviLista.length"
        class="agenda__none"
      >
        Nessun servizio attivo sul tuo contratto. Parlane con Simba.
      </div>

      <div
        v-else
        class="agenda__opts"
      >
        <button
          v-for="s in serviziAttiviLista"
          :key="s.value"
          type="button"
          class="agenda__opt"
          @click="creaPer(s.value)"
        >
          <span class="agenda__optName">{{ s.label }}</span>
          <span class="agenda__optDesc">{{ s.desc }}</span>
        </button>

        <div
          v-for="s in serviziSpentiLista"
          :key="s.value"
          class="agenda__opt agenda__opt--off"
        >
          <span class="agenda__optName">{{ s.label }}</span>
          <span class="agenda__optDesc">Non attivo sul tuo contratto</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { agendaInWindow } from '@/services/agendaAPI';
import { buildRows, rottaDettaglio } from '@/services/agendaRows';
import { rottaCreazione, serviziAttivi, serviziSpenti } from '@/services/servizi';
import { isLoggedStore } from '@/store/logged';
import AgendaRighe from '@/Components/AgendaRighe.vue';

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

// Colori dei servizi: gli stessi token del gestionale admin, così una
// prenotazione ha lo stesso colore nei due gestionali.
const COLORI = {
  pickup: 'var(--svc-pickup)',
  pickup_loaner: 'var(--svc-pickup)',
  delivery: 'var(--svc-pickup)',
  escort: 'var(--svc-transfer)',
  restaurant: 'var(--svc-notturno)',
  journey: 'var(--svc-fleet)',
};

const iso = (d) => d.toISOString().slice(0, 10);

export default {
  name: 'AgendaPage',
  components: { AgendaRighe },

  setup() {
    const router = useRouter();
    // Serve solo a sapere quali servizi il partner può creare, quando si
    // clicca un giorno libero.
    const store = isLoggedStore();
    const route = useRoute();
    const oggi = new Date();

    // -------------------------------------------------------------------
    //  VISTA, GIORNO E MESE STANNO NELL'URL: `?vista=elenco&giorno=…&mese=…`.
    //
    //  Così cliccare «Agenda» nel menu riapre il calendario (`/agenda` senza
    //  parametri) e «indietro» del browser rimette il giorno che si guardava.
    // -------------------------------------------------------------------
    const ISO_GIORNO = /^\d{4}-\d{2}-\d{2}$/;
    const ISO_MESE = /^(\d{4})-(\d{2})$/;
    const parametro = (k) => String(route.query[k] || '');

    const vista = computed(() => (parametro('vista') === 'elenco' ? 'elenco' : 'calendario'));
    const giornoScelto = computed(() => (ISO_GIORNO.test(parametro('giorno')) ? parametro('giorno') : ''));
    // Il giorno da cui si è passati a «tutto il mese»: ci riporta il pulsante.
    const giornoDiPartenza = computed(() => (ISO_GIORNO.test(parametro('da')) ? parametro('da') : ''));

    const meseScelto = computed(() => {
      const m = ISO_MESE.exec(parametro('mese')) || ISO_MESE.exec(giornoScelto.value.slice(0, 7));
      return m ? { anno: Number(m[1]), mese: Number(m[2]) } : null;
    });
    const anno = computed(() => meseScelto.value?.anno ?? oggi.getFullYear());
    const mese = computed(() => meseScelto.value?.mese ?? oggi.getMonth() + 1); // 1-based
    const meseIso = (a = anno.value, m = mese.value) => `${a}-${String(m).padStart(2, '0')}`;

    // push resta nella cronologia («indietro» ci torna), replace no: cambiare
    // mese o vista non deve riempire la cronologia di passaggi.
    const vai = (query, { sostituisci = false } = {}) => {
      const pulita = Object.fromEntries(Object.entries(query).filter(([, v]) => v));
      const destinazione = { path: '/agenda', query: pulita };
      return sostituisci ? router.replace(destinazione) : router.push(destinazione);
    };

    const mostraVista = (v) =>
      vai({ vista: v === 'elenco' ? 'elenco' : '', mese: meseIso() }, { sostituisci: true });
    const servizio = ref('tutti');
    const stato = ref('tutti');
    const cerca = ref('');
    const loading = ref(false);
    const troncato = ref(false);
    const righe = ref([]);

    const etichettaMese = computed(() => `${MESI[mese.value - 1]} ${anno.value}`);

    // La finestra è il mese intero: la stessa per le due viste, così passare
    // da calendario a elenco non cambia quello che si sta guardando.
    const finestra = computed(() => ({
      from: iso(new Date(Date.UTC(anno.value, mese.value - 1, 1))),
      to: iso(new Date(Date.UTC(anno.value, mese.value, 0))),
    }));

    const carica = async () => {
      loading.value = true;
      try {
        const { from, to } = finestra.value;
        const dati = await agendaInWindow(from, to);
        righe.value = buildRows(dati);
        troncato.value = dati.truncated;
      } catch (e) {
        // L'errore parlante lo produce già `handleHttpError` nell'interceptor.
        righe.value = [];
        troncato.value = false;
        console.error('Agenda non caricata:', e);
        ElMessage({ message: 'Agenda non caricata', type: 'error' });
      } finally {
        loading.value = false;
      }
    };

    // -------------------------------------------------------------------
    //  CLICK SU UN GIORNO
    //
    //  Giorno vuoto  -> si sceglie il servizio e si va a crearlo, con la data
    //                   gia' in mano.
    //  Giorno pieno  -> si apre l'elenco ristretto a quel giorno, da cui si
    //                   puo' comunque aggiungere.
    //
    //  E' la differenza fra «cosa succede martedi'» e «voglio fare qualcosa
    //  martedi'»: la stessa cella risponde a entrambe senza chiedere prima
    //  quale delle due si intende.
    // -------------------------------------------------------------------
    // Due date, non una sola, perche' rispondono a due domande diverse:
    //   `giornoScelto`   restringe l'ELENCO (si scioglie con «Mostra tutto il
    //                    mese»), e vale solo per i giorni che hanno righe;
    //   `giornoCreazione` e' la data con cui si va a CREARE.
    // Con una sola data, un giorno libero cliccato filtrerebbe anche l'elenco.
    const giornoCreazione = ref('');
    const sceltaAperta = ref(false);

    const serviziAttiviLista = computed(() => serviziAttivi(store));
    const serviziSpentiLista = computed(() => serviziSpenti(store));

    // Il giorno di una riga, in forma `YYYY-MM-DD` e in ora LOCALE: e' cosi'
    // che il calendario raggruppa le righe nelle celle, e i due criteri devono
    // combaciare, altrimenti una riga di sera finisce nella cella sbagliata.
    const isoDiRiga = (r) => {
      const v = r.start || r.date;
      if (!v) return '';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;
    };

    const esteso = (iso) => {
      if (!iso) return '';
      const [a, m, g] = iso.split('-');
      return new Date(Number(a), Number(m) - 1, Number(g)).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    };

    const giornoEsteso = computed(() => esteso(giornoScelto.value));
    const giornoCreazioneEsteso = computed(() => esteso(giornoCreazione.value));

    // «Tutto il mese»: il pulsante a sinistra riporta al giorno da cui si
    // veniva. Se si è arrivati dall'interruttore Elenco riporta a OGGI, purché
    // oggi sia nel mese mostrato; altrimenti al calendario.
    const giornoRitorno = computed(() => {
      if (giornoDiPartenza.value) return giornoDiPartenza.value;
      const g = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}-${String(
        oggi.getDate()
      ).padStart(2, '0')}`;
      return g.startsWith(meseIso()) ? g : '';
    });
    const giornoRitornoEsteso = computed(() => esteso(giornoRitorno.value));

    const tornaAlCalendario = () => vai({ mese: meseIso() });
    const mostraMese = () => vai({ vista: 'elenco', mese: meseIso(), da: giornoScelto.value });
    const tornaAlGiorno = () => vai({ vista: 'elenco', giorno: giornoRitorno.value, mese: meseIso() });

    const etichettaGiorno = (c) =>
      c.eventi.length
        ? `${c.numero}: ${c.eventi.length} in agenda, apri l'elenco`
        : `${c.numero}: libero, aggiungi una prenotazione`;

    const apriGiorno = (c) => {
      giornoCreazione.value = c.iso;
      if (c.eventi.length) {
        // Giorno pieno: si guarda cosa c'e'. Il filtro resta dichiarato nella
        // barra, e da li' si puo' comunque aggiungere o tornare al calendario.
        vai({ vista: 'elenco', giorno: c.iso, mese: c.iso.slice(0, 7) });
        return;
      }
      // Giorno libero: non c'e' niente da guardare, si passa direttamente alla
      // scelta di cosa aggiungere. L'elenco NON viene filtrato.
      sceltaAperta.value = true;
    };

    // Dalla barra dell'elenco: si aggiunge nel giorno che si sta guardando.
    const aggiungiInGiornata = () => {
      giornoCreazione.value = giornoScelto.value;
      sceltaAperta.value = true;
    };

    const creaPer = (servizio) => {
      sceltaAperta.value = false;
      router.push(rottaCreazione(servizio, giornoCreazione.value));
    };

    // Dove porta una riga lo decide `rottaDettaglio`, condivisa con la Home.
    const apribile = (r) => Boolean(rottaDettaglio(r));
    const apri = (r) => {
      const rotta = rottaDettaglio(r);
      if (rotta) router.push(rotta);
    };

    const spostaMese = (delta) => {
      let m = mese.value + delta;
      let y = anno.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      // Cambiando mese il filtro sul giorno non ha più senso: si scioglie.
      vai({ vista: vista.value === 'elenco' ? 'elenco' : '', mese: meseIso(y, m) }, { sostituisci: true });
    };

    watch([anno, mese], carica);
    onMounted(carica);

    // ---- filtri -------------------------------------------------------
    // I servizi elencati sono SOLO quelli presenti nel periodo: proporre un
    // filtro che non ha righe fa cercare qualcosa che non c'è.
    const serviziDisponibili = computed(() => {
      const conta = {};
      righe.value.forEach((r) => { conta[r.service] = (conta[r.service] || 0) + 1; });
      const nomi = {
        pickup: 'Pickup & Delivery',
        pickup_loaner: 'Pickup & Delivery',
        delivery: 'Pickup & Delivery',
        escort: 'Transfer',
        restaurant: 'Notturno',
        journey: 'Spostamenti interni',
      };
      // pickup, pickup_loaner e delivery sono lo stesso servizio a schermo
      const gruppo = (s) => (['pickup', 'pickup_loaner', 'delivery'].includes(s) ? 'pickup' : s);
      const per = {};
      Object.entries(conta).forEach(([s, n]) => {
        const g = gruppo(s);
        per[g] = (per[g] || 0) + n;
      });
      return Object.entries(per).map(([value, n]) => ({
        value, n, label: nomi[value] || value, color: COLORI[value] || 'var(--state-muted)',
      }));
    });

    const inServizio = (r) => {
      if (servizio.value === 'tutti') return true;
      if (servizio.value === 'pickup') {
        return ['pickup', 'pickup_loaner', 'delivery'].includes(r.service);
      }
      return r.service === servizio.value;
    };

    const righeFiltrate = computed(() => {
      const q = cerca.value.trim().toLowerCase();
      return righe.value.filter((r) => {
        if (!inServizio(r)) return false;
        // Restrizione a un solo giorno, quando si arriva da una cella piena.
        if (giornoScelto.value && isoDiRiga(r) !== giornoScelto.value) return false;
        if (stato.value === 'attive' && r.stateKind === 'muted') return false;
        if (stato.value === 'concluse' && r.stateKind !== 'muted') return false;
        if (!q) return true;
        return `${r.plate} ${r.customer} ${r.shortId} ${r.carLabel}`
          .toLowerCase()
          .includes(q);
      });
    });

    // ---- griglia del calendario ---------------------------------------
    // Settimana che inizia di LUNEDÌ, come si legge un'agenda in Italia.
    const celle = computed(() => {
      const primo = new Date(anno.value, mese.value - 1, 1);
      const scarto = (primo.getDay() + 6) % 7; // 0 = lunedì
      const inizio = new Date(anno.value, mese.value - 1, 1 - scarto);
      const perGiorno = {};
      righe.value.filter(inServizio).forEach((r) => {
        const v = r.start || r.date;
        if (!v) return;
        const d = new Date(v);
        const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        (perGiorno[k] = perGiorno[k] || []).push(r);
      });
      const oggiK = (() => {
        const n = new Date();
        return `${n.getFullYear()}-${n.getMonth()}-${n.getDate()}`;
      })();
      return Array.from({ length: 42 }, (_, i) => {
        const d = new Date(inizio);
        d.setDate(inizio.getDate() + i);
        const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        return {
          key: k,
          // ⚠️ `iso` si costruisce dai pezzi della data LOCALE, non con
          // `toISOString()`: quello converte in UTC e a Roma sposterebbe il
          // giorno indietro per tutte le celle (mezzanotte locale = 22:00 del
          // giorno prima in UTC). Cliccando il 15 si finirebbe a creare il 14.
          iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
            d.getDate()
          ).padStart(2, '0')}`,
          numero: d.getDate(),
          nelMese: d.getMonth() === mese.value - 1,
          oggi: k === oggiK,
          eventi: perGiorno[k] || [],
        };
      });
    });

    // ---- formattazione -------------------------------------------------
    const ora = (v) => (v ? new Date(v).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—');
    const coloreServizio = (s) => COLORI[s] || 'var(--state-muted)';

    return {
      vista, servizio, stato, cerca, loading, troncato,
      righe, righeFiltrate, serviziDisponibili, celle,
      etichettaMese, spostaMese, apri, apribile, ora, coloreServizio, mostraVista,
      giornoScelto, sceltaAperta, giornoEsteso, giornoCreazioneEsteso,
      etichettaGiorno, apriGiorno, aggiungiInGiornata, creaPer,
      giornoRitorno, giornoRitornoEsteso, tornaAlCalendario, mostraMese, tornaAlGiorno,
      serviziAttiviLista, serviziSpentiLista,
    };
  },
};
</script>

<style scoped>
.agenda { padding: 4px 8px 32px; text-align: left; }

.agenda__head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.agenda__title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.015em; }
.agenda__spacer { flex-grow: 1; }

.agenda__views { display: flex; background: var(--color-bg-tertiary); border: 1px solid var(--color-border); border-radius: 6px; padding: 2px; gap: 2px; }
.agenda__view { border: 0; background: none; border-radius: 4px; padding: 6px 14px; font: inherit; font-size: 13px; color: var(--color-text-secondary); cursor: pointer; }
.agenda__view.is-active { background: #fff; color: var(--color-text-primary); font-weight: 600; box-shadow: var(--el-box-shadow-lighter); }

.agenda__month { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 6px 12px; }
.agenda__nav { border: 0; background: none; font-size: 15px; color: var(--color-text-muted); cursor: pointer; line-height: 1; }
.agenda__monthLbl { font-size: 13.5px; font-weight: 600; min-width: 128px; text-align: center; }

.agenda__filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.agenda__pill { display: inline-flex; align-items: center; gap: 7px; background: #fff; border: 1px solid var(--color-border); border-radius: 20px; padding: 6px 14px; font: inherit; font-size: 12.5px; cursor: pointer; }
.agenda__pill.is-on { background: var(--color-text-primary); border-color: var(--color-text-primary); color: #fff; font-weight: 600; }
.agenda__dot { width: 8px; height: 8px; border-radius: 2px; }
.agenda__count { color: var(--color-text-secondary); font-variant-numeric: tabular-nums; }
.agenda__pill.is-on .agenda__count { color: rgba(255, 255, 255, 0.65); }

.agenda__search input { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 8px 12px; font: inherit; font-size: 13px; min-width: 240px; }
.agenda__select { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 8px 12px; font: inherit; font-size: 13px; }

.agenda__warn { display: flex; align-items: center; gap: 10px; background: var(--state-attn-bg); border: 1px solid var(--state-attn-line); color: var(--state-attn); border-radius: 6px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; }
.agenda__warnIco { font-size: 15px; }

/* ---- calendario ---- */
.agenda__cal { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 8px; }
.agenda__dow { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-secondary); text-align: center; padding-bottom: 4px; }
.agenda__day { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; min-height: 104px; padding: 8px 9px; display: flex; flex-direction: column; gap: 4px; }
.agenda__day.is-out { background: var(--color-bg-secondary); }
.agenda__day.is-out .agenda__num { color: var(--color-border); }
.agenda__day.is-today { border: 2px solid #232c51; }
.agenda__num { font-size: 12px; font-weight: 600; color: var(--color-text-secondary); font-variant-numeric: tabular-nums; }
.agenda__ev { font-size: 11px; background: var(--color-bg-secondary); border-left: 3px solid var(--state-muted); border-radius: 4px; padding: 3px 6px; display: flex; gap: 5px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.agenda__evH { font-weight: 650; font-variant-numeric: tabular-nums; }
.agenda__more { font-size: 11px; color: var(--color-text-muted); padding-left: 6px; }

/* ---- giorno cliccabile ----
   Il «+» compare solo sulle celle vuote e solo al passaggio del mouse: dice
   che il giorno e' un punto di partenza, senza riempire di simboli un mese
   intero. Sulle celle piene il segnale e' il contenuto stesso. */
.agenda__day.is-clickable { cursor: pointer; position: relative; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.agenda__day.is-clickable:hover { border-color: var(--color-primary); box-shadow: 0 2px 10px rgba(75, 48, 242, 0.12); }
.agenda__day.is-clickable:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.agenda__add { position: absolute; right: 8px; top: 6px; font-size: 15px; font-weight: 700; line-height: 1; color: var(--color-primary); opacity: 0; transition: opacity 0.15s ease; }
.agenda__day.is-clickable:hover .agenda__add,
.agenda__day.is-clickable:focus-visible .agenda__add { opacity: 1; }

/* ---- barra del giorno selezionato ---- */
.agenda__daybar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; }
.agenda__back { display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--color-text-primary); background: var(--color-bg-secondary); border: 1px solid var(--color-border); border-radius: 6px; padding: 6px 11px; cursor: pointer; }
.agenda__back:hover { border-color: var(--color-primary); color: var(--color-primary); }
.agenda__back:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.agenda__backDay { text-transform: capitalize; }
.agenda__daybarTxt { font-size: 13px; color: var(--color-text-secondary); }
.agenda__daybarTxt strong { color: var(--color-text-primary); text-transform: capitalize; }
.agenda__daybarBtn { font: inherit; font-size: 12.5px; font-weight: 600; color: #fff; background: var(--color-primary); border: none; border-radius: 6px; padding: 7px 13px; cursor: pointer; }
.agenda__daybarBtn:hover { background: var(--color-primary-light); }
.agenda__daybarLink { font: inherit; font-size: 12.5px; font-weight: 600; color: var(--color-text-secondary); background: none; border: none; cursor: pointer; text-decoration: underline; }
.agenda__daybarLink:hover { color: var(--color-text-primary); }

/* ---- scelta del servizio ---- */
.agenda__opts { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 12px; }
.agenda__opt { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; text-align: left; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 14px 16px; font: inherit; cursor: pointer; transition: border-color 0.15s ease; }
.agenda__opt:hover { border-color: var(--color-primary); }
.agenda__opt--off { background: var(--color-bg-secondary); border-style: dashed; cursor: default; }
.agenda__opt--off .agenda__optName,
.agenda__opt--off .agenda__optDesc { color: var(--color-text-muted); }
.agenda__optName { font-size: 14.5px; font-weight: 600; }
.agenda__optDesc { font-size: 12.5px; line-height: 1.45; color: var(--color-text-secondary); }
.agenda__none { font-size: 13px; color: var(--color-text-muted); }
</style>
